import React, { createContext, useContext, useState, useEffect } from 'react';

// Upload preferences context
const UploadPreferencesContext = createContext();

// Default preferences
const defaultPreferences = {
  restrictToAnalyzableFiles: false,
  allowAdditionalFileTypes: true,
  showAnalysisWarning: true,
  autoSelectUploadedDocuments: true,
  maxFileSize: 10 * 1024 * 1024, // 10MB
  maxFilesPerUpload: 5,
  showUploadProgress: true,
  confirmBeforeUpload: false
};

// Preferences provider component
export const UploadPreferencesProvider = ({ children }) => {
  const [preferences, setPreferences] = useState(() => {
    // Load preferences from localStorage or use defaults
    const savedPreferences = localStorage.getItem('uploadPreferences');
    return savedPreferences ? { ...defaultPreferences, ...JSON.parse(savedPreferences) } : defaultPreferences;
  });

  // Save preferences to localStorage when they change
  useEffect(() => {
    localStorage.setItem('uploadPreferences', JSON.stringify(preferences));
  }, [preferences]);

  const updatePreference = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetPreferences = () => {
    setPreferences(defaultPreferences);
    localStorage.removeItem('uploadPreferences');
  };

  const value = {
    preferences,
    updatePreference,
    resetPreferences
  };

  return (
    <UploadPreferencesContext.Provider value={value}>
      {children}
    </UploadPreferencesContext.Provider>
  );
};

// Hook to use upload preferences
export const useUploadPreferences = () => {
  const context = useContext(UploadPreferencesContext);
  if (!context) {
    throw new Error('useUploadPreferences must be used within an UploadPreferencesProvider');
  }
  return context;
};
